import React from 'react';
import PropTypes from 'prop-types';

import {Box} from 'views/components';
import {translate} from 'views/module';

import MetricCard from '../shared/MetricCard';
import SectionHeading from '../shared/SectionHeading';
import CardGridSkeleton from '../shared/skeletons/CardGridSkeleton';

import formatters from '../shared/formatters';

const ClientOverviewSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
    previousSnapshot,
  } = props;

  const overviewCards = React.useMemo(() => {
    // The current ISO week is the first forecast bucket of each snapshot, so
    // "this week" compares each snapshot's own current week.
    const getCurrentWeek = (snapshot) => {
      const weeks = snapshot?.forecast?.weeks || {};
      const weekKeys = formatters.sortWeekKeysAscending(weeks);

      return weekKeys.length ? weeks[weekKeys[0]] : null;
    };
    const currentTotals = currentSnapshot?.forecast?.totals || null;
    const previousTotals = previousSnapshot?.forecast?.totals || null;
    const currentWeek = getCurrentWeek(currentSnapshot);
    const previousWeek = getCurrentWeek(previousSnapshot);

    return [
      {
        key: 'ACTIVE_CLIENTS',
        label: translate('metrics.ClientMetrics.ClientOverview.ActiveClients'),
        caption: translate('metrics.ClientMetrics.ClientOverview.ActiveClientsCaption'),
        comparison: formatters.buildComparison(
            currentTotals?.activeClientCount,
            previousTotals?.activeClientCount,
            {kind: 'count', goodDirection: 'up'},
        ),
      },
      {
        key: 'SCHEDULED_CLIENTS_THIS_WEEK',
        label: translate('metrics.ClientMetrics.ClientOverview.ScheduledClientsThisWeek'),
        caption: translate('metrics.ClientMetrics.ClientOverview.ScheduledClientsThisWeekCaption'),
        comparison: formatters.buildComparison(
            currentWeek?.activeClientCount,
            previousWeek?.activeClientCount,
            {kind: 'count', goodDirection: 'up'},
        ),
      },
      {
        key: 'CAREGIVERS',
        label: translate('metrics.ClientMetrics.ClientOverview.Caregivers'),
        caption: translate('metrics.ClientMetrics.ClientOverview.CaregiversCaption'),
        comparison: formatters.buildComparison(
            currentTotals?.caregiverCount,
            previousTotals?.caregiverCount,
            {kind: 'count', goodDirection: 'up'},
        ),
      },
      {
        key: 'MISSING_CLIENT_RATES',
        label: translate('metrics.ClientMetrics.ClientOverview.MissingClientRates'),
        caption: translate('metrics.ClientMetrics.ClientOverview.MissingClientRatesCaption'),
        comparison: formatters.buildComparison(
            currentSnapshot?.dataQuality?.missingClientRateCount,
            previousSnapshot?.dataQuality?.missingClientRateCount,
            {kind: 'count', goodDirection: 'down'},
        ),
      },
    ];
  }, [currentSnapshot, previousSnapshot]);

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.ClientOverview.SectionTitle' />
      {
        isLoading &&
        <CardGridSkeleton cardCount={4} />
      }
      {
        !isLoading &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'},
            gap: 1.75,
          }}
        >
          {
            overviewCards.map((card) => (
              <MetricCard
                key={card.key}
                label={card.label}
                caption={card.caption}
                value={card.comparison.value}
                previousValue={card.comparison.previous}
                chip={card.comparison.chip}
              />
            ))
          }
        </Box>
      }
    </Box>
  );
};

ClientOverviewSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
  previousSnapshot: PropTypes.object,
};

export default React.memo(ClientOverviewSection);
